<form [formGroup]="form" (ngSubmit)="create()">
  <nb-card class="dialog">
    <nb-card-header>
      <h4>{{ "pages.creator.acDialog.title" | translate }}</h4>
    </nb-card-header>
    <nb-card-body>
      <p class="intro">{{ "pages.creator.acDialog.description" | translate }}</p>

      <div class="field">
        <span class="label" id="ac-kind-label">{{ "pages.creator.acDialog.kind" | translate }}</span>
        <app-segmented-control
          formControlName="kind"
          [options]="kinds"
          [ariaLabel]="'pages.creator.acDialog.kind' | translate"
        ></app-segmented-control>

        <!-- what the kind means: chapters to scroll vs. slides to click through -->
        <div class="kind-info">
          <span class="kind-demo" aria-hidden="true">
            <span *ngIf="form.controls.kind.value !== 'microlearning'" class="demo-el">
              <span class="demo-el__nav">
                <span class="demo-el__marker"></span>
                <span></span><span></span><span></span><span></span>
              </span>
              <span class="demo-el__page">
                <span class="demo-el__scroll">
                  <span class="demo-heading"></span>
                  <span class="demo-line"></span><span class="demo-line"></span><span class="demo-line demo-line--short"></span>
                  <span class="demo-image"></span>
                  <span class="demo-line"></span><span class="demo-line"></span><span class="demo-line demo-line--short"></span>
                  <span class="demo-heading"></span>
                  <span class="demo-line"></span><span class="demo-line"></span><span class="demo-line"></span>
                  <span class="demo-line demo-line--short"></span>
                </span>
              </span>
            </span>
            <span *ngIf="form.controls.kind.value === 'microlearning'" class="demo-mi">
              <span class="demo-mi__stage">
                <span class="demo-mi__track">
                  <span class="demo-mi__slide"><span class="demo-heading"></span><span class="demo-image"></span></span>
                  <span class="demo-mi__slide"><span class="demo-heading"></span><span class="demo-line"></span><span class="demo-line demo-line--short"></span></span>
                  <span class="demo-mi__slide"><span class="demo-heading"></span><span class="demo-mi__choices"><span></span><span></span></span></span>
                  <span class="demo-mi__slide"><span class="demo-heading"></span><span class="demo-image"></span></span>
                </span>
              </span>
              <span class="demo-mi__bar">
                <span class="demo-mi__dots"><span></span><span></span><span></span><span></span></span>
                <span class="demo-mi__next"></span>
              </span>
            </span>
          </span>
          <p class="kind-hint">
            {{ (form.controls.kind.value === "microlearning" ? "pages.creator.acDialog.microHint" : "pages.creator.acDialog.elearningHint") | translate }}
          </p>
        </div>
      </div>

      <div class="field">
        <label class="label" for="ac-title">{{ "pages.creator.acDialog.name" | translate }}</label>
        <input
          nbInput
          fullWidth
          id="ac-title"
          type="text"
          formControlName="title"
          maxlength="200"
          [placeholder]="'pages.creator.acDialog.namePlaceholder' | translate"
        />
      </div>

      <div class="field">
        <span class="label">{{ "pages.creator.acDialog.look" | translate }}</span>
        <div class="looks" role="radiogroup" [attr.aria-label]="'pages.creator.acDialog.look' | translate">
          <button
            *ngFor="let look of looks"
            type="button"
            class="look"
            role="radio"
            [class.al-pressed]="form.controls.kitId.value === look.id"
            [attr.aria-checked]="form.controls.kitId.value === look.id"
            (click)="pickLook(look.id)"
          >
            <!-- one step of a course in the kit's own blocks (values from the course editor's seed kits) -->
            <span class="look__preview" [ngClass]="'look__preview--' + look.id" aria-hidden="true">
              <span class="look__progress"><span></span></span>
              <span class="look__page">
                <span class="look__heading">{{ "pages.creator.acDialog.sample.heading" | translate }}</span>
                <span class="look__text">{{ "pages.creator.acDialog.sample.text" | translate }}</span>
                <span class="look__note">{{ "pages.creator.acDialog.sample.note" | translate }}</span>
                <span class="look__options">
                  <span class="look__option look__option--picked"><i></i>{{ "pages.creator.acDialog.sample.optionA" | translate }}</span>
                  <span class="look__option"><i></i>{{ "pages.creator.acDialog.sample.optionB" | translate }}</span>
                </span>
                <span class="look__button">{{ "pages.creator.acDialog.sample.next" | translate }}</span>
              </span>
              <span class="look__rail"><i></i><i class="look__rail-current"></i><i></i><i></i></span>
            </span>
            <span class="look__name">{{ look.label }}</span>
            <span class="look__hint">{{ look.hint }}</span>
          </button>
        </div>
        <nb-select
          *ngIf="ownKits.length"
          class="own-kits"
          [placeholder]="'pages.creator.acDialog.ownKit' | translate"
          [selected]="form.controls.kitId.value"
          (selectedChange)="pickLook($event)"
          [attr.aria-label]="'pages.creator.acDialog.ownKit' | translate"
        >
          <nb-option *ngFor="let kit of ownKits" [value]="kit.id">{{ kit.name }}</nb-option>
        </nb-select>
      </div>

      <div class="row">
        <div class="field">
          <label class="label" for="ac-lang">{{ "pages.creator.acDialog.lang" | translate }}</label>
          <nb-select id="ac-lang" formControlName="lang" fullWidth>
            <nb-option value="de">{{ "pages.creator.acDialog.langs.de" | translate }}</nb-option>
            <nb-option value="en">{{ "pages.creator.acDialog.langs.en" | translate }}</nb-option>
          </nb-select>
        </div>
        <div class="field">
          <label class="label" for="ac-voice">{{ "pages.creator.acDialog.voice" | translate }}</label>
          <nb-select id="ac-voice" formControlName="voice" fullWidth>
            <nb-option value="du">{{ "pages.creator.acDialog.voices.du" | translate }}</nb-option>
            <nb-option value="sie">{{ "pages.creator.acDialog.voices.sie" | translate }}</nb-option>
          </nb-select>
        </div>
      </div>
    </nb-card-body>
    <nb-card-footer>
      <button type="button" nbButton status="danger" outline (click)="cancel()" [disabled]="saving">
        {{ "buttons.cancel" | translate }}
      </button>
      <button type="submit" nbButton status="primary" [nbSpinner]="saving" [disabled]="saving">
        {{ "pages.creator.acDialog.create" | translate }}
      </button>
    </nb-card-footer>
  </nb-card>
</form>
